import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Modal,
  Form,
  Button,
  InputGroup,
  Alert,
  Spinner,
} from "@themesberg/react-bootstrap";
import {
  FiEye,
  FiEyeOff,
  FiCheck,
  FiX,
  FiCopy,
  FiArrowRight,
} from "react-icons/fi";
import {
  userCheckSubIdUsername,
  userCreateSubId,
  userGetEligibleSubIdPlacements,
} from "../../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faLock } from "@fortawesome/free-solid-svg-icons";

const USERNAME_REGEX = /^[a-z0-9_]{4,50}$/;

const emptyForm = {
  name: "",
  username: "",
  placement_referral_number: "",
  password: "",
  password_confirmation: "",
};

export default function AddIdModal({ show, onHide, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState({
    status: "idle",
    message: "",
  });
  const [successData, setSuccessData] = useState(null);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const debounceRef = useRef(null);

  const resetModal = () => {
    setForm(emptyForm);
    setErrors({});
    setServerError("");
    setUsernameCheck({ status: "idle", message: "" });
    setSuccessData(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setCopiedReferral(false);
  };

  const handleClose = () => {
    resetModal();
    onHide();
  };

  useEffect(() => {
    if (!show) {
      resetModal();
    }
  }, [show]);

  useEffect(() => {
    if (!show || successData) return;

    const loadPlacements = async () => {
      try {
        setPlacementsLoading(true);
        const response = await userGetEligibleSubIdPlacements();
        const list = response?.data?.data?.placements;
        setPlacements(Array.isArray(list) ? list : []);
      } catch (error) {
        setPlacements([]);
        toast.error(
          error?.response?.data?.message || "Unable to load eligible placements."
        );
      } finally {
        setPlacementsLoading(false);
      }
    };

    loadPlacements();
  }, [show, successData]);

  useEffect(() => {
    const username = form.username.trim().toLowerCase();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!username) {
      setUsernameCheck({ status: "idle", message: "" });
      return;
    }

    if (!USERNAME_REGEX.test(username)) {
      setUsernameCheck({
        status: "invalid",
        message: "Only lowercase letters, numbers, and underscores allowed",
      });
      return;
    }

    setUsernameCheck({ status: "checking", message: "Checking availability..." });

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await userCheckSubIdUsername(username);
        const data = response?.data || {};
        setUsernameCheck({
          status: data.available ? "available" : "taken",
          message: data.message || (data.available ? "Username is available" : "This username is already taken"),
        });
      } catch (error) {
        setUsernameCheck({
          status: "taken",
          message: error?.response?.data?.message || "Unable to check username",
        });
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [form.username]);

    const handleChange = (e) => {
  const { name, value } = e.target;

  setForm((prev) => ({
    ...prev,
    [name]: value,
  }));

  setErrors((prev) => {
    const newErrors = { ...prev };

    // Clear error when typing
    delete newErrors[name];

    // LIVE confirm password validation
    if (name === "password_confirmation") {
      if (value && value !== form.password) {
        newErrors.password_confirmation = "Passwords do not match";
      } else {
        delete newErrors.password_confirmation;
      }
    }

    // If password changes, re-check confirm password
    if (name === "password" && form.password_confirmation) {
      if (form.password_confirmation !== value) {
        newErrors.password_confirmation = "Passwords do not match";
      } else {
        delete newErrors.password_confirmation;
      }
    }

    return newErrors;
  });
};


  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Full name is required.";
    if (!form.username.trim()) {
      nextErrors.username = "Username is required.";
    } else if (!USERNAME_REGEX.test(form.username.trim().toLowerCase())) {
      nextErrors.username = "Only lowercase letters, numbers, and underscores allowed (min 4 characters).";
    } else if (usernameCheck.status !== "available") {
      nextErrors.username = usernameCheck.message || "Please choose an available username.";
    }
    if (!form.placement_referral_number.trim()) {
      nextErrors.placement_referral_number = "Select an eligible placement user.";
    }
    // if (!form.password) {
    //   nextErrors.password = "Password is required.";
    // } else if (form.password.length < 6) {
    //   nextErrors.password = "Password must be at least 6 characters.";
    // }
    // if (!form.password_confirmation) {
    //   nextErrors.password_confirmation = "Please confirm the password.";
    // } else if (form.password !== form.password_confirmation) {
    //   nextErrors.password_confirmation = "Passwords do not match.";
    // }

    if (!form.password) {
      nextErrors.password = "Password is required.";
    } else if (!isPasswordValid(form.password)) {
      nextErrors.password =
        "Password must contain uppercase, lowercase, number, special character and be at least 8 characters.";
    }
    const confirmError = getConfirmPasswordError(
      form.password,
      form.password_confirmation
    );
    if (confirmError) {
      nextErrors.password_confirmation = confirmError;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    try {
      setSubmitting(true);
      const response = await userCreateSubId({
        name: form.name.trim(),
        username: form.username.trim().toLowerCase(),
        placement_referral_number: form.placement_referral_number.trim().toUpperCase(),
        password: form.password,
        password_confirmation: form.password_confirmation,
      });

      setSuccessData(response?.data?.data || null);
      toast.success(response?.data?.message || "ID created successfully");
      onCreated?.();
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to create ID";
      setServerError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyReferral = async () => {
    if (!successData?.referral_number) return;
    try {
      await navigator.clipboard.writeText(successData.referral_number);
      setCopiedReferral(true);
      setTimeout(() => setCopiedReferral(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

   const getPasswordRules = (password) => {
    const value = String(password || "");
    return {
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /\d/.test(value),
      special: /[@$!%*?&]/.test(value),
      minLength: value.length >= 8,
    };
  };

  const isPasswordValid = (password) => {
    const rules = getPasswordRules(password);
    return Object.values(rules).every(Boolean);
  };

   const getConfirmPasswordError = (password, confirmPassword) => {
    const confirm = String(confirmPassword || "");
    if (!confirm) return "Confirm password is required.";
    if (password !== confirm) return "Passwords do not match.";
    return "";
  };


  const loginUrl = `${window.location.origin}/sign-in`;
  
  const passwordRules = getPasswordRules(form.password);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{successData ? "ID Created Successfully" : "Add New ID"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {successData ? (
          <div className="my-ids-success-panel">
            <div className="my-ids-success-icon">
              <FiCheck />
            </div>
            <h5 className="fw-bold mb-3">Your new ID details</h5>

            <div className="text-start bg-light rounded p-3 mb-3">
              <div className="d-flex justify-content-between py-1">
                <span className="text-muted">Username</span>
                <span className="fw-semibold">{successData.username}</span>
              </div>
              <div className="d-flex justify-content-between py-1">
                <span className="text-muted">ID</span>
                <span className="fw-semibold">{successData.custom_user_id}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1">
                <span className="text-muted">Referral</span>
                <span className="fw-semibold d-flex align-items-center gap-1">
                  {successData.referral_number}
                  <button
                    type="button"
                    className="my-ids-copy-btn"
                    onClick={handleCopyReferral}
                  >
                    {copiedReferral ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </span>
              </div>
            </div>

            <div className="my-ids-info-banner text-start">
              Share these credentials with the person who will operate this ID.
              They can log in at {loginUrl} using the username and password you set.
            </div>

            <div className="d-flex gap-2 justify-content-center flex-wrap">
              <Button
                variant="outline-primary"
                className="my-ids-btn-outline"
                onClick={() => {
                  setSuccessData(null);
                  setForm(emptyForm);
                  setUsernameCheck({ status: "idle", message: "" });
                }}
              >
                Add Another ID
              </Button>
              <Button className="my-ids-btn-primary" onClick={handleClose}>
                View My IDs
              </Button>
            </div>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <div className="my-ids-info-banner">
              Sub-IDs can only be placed at Level 3, Level 6, or Level 9.
              Commission starts only after a course is purchased.
            </div>

            {serverError && (
              <Alert variant="danger" className="py-2">
                {serverError}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Full Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={form.name}
                placeholder="Enter full name"
                isInvalid={!!errors.name}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, name: e.target.value }));
                  setErrors((prev) => ({ ...prev, name: "" }));
                }}
              />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Username <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={form.username}
                placeholder="username"
                isInvalid={!!errors.username || usernameCheck.status === "taken" || usernameCheck.status === "invalid"}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                  }));
                  setErrors((prev) => ({ ...prev, username: "" }));
                }}
              />
              {usernameCheck.status !== "idle" && (
                <Form.Text
                  className={`my-ids-username-check d-flex align-items-center gap-1 ${usernameCheck.status}`}
                >
                  {usernameCheck.status === "checking" && <Spinner size="sm" animation="border" />}
                  {usernameCheck.status === "available" && <FiCheck />}
                  {(usernameCheck.status === "taken" || usernameCheck.status === "invalid") && <FiX />}
                  {usernameCheck.message}
                </Form.Text>
              )}
              <Form.Text className="text-muted">
                Only lowercase letters, numbers, underscores. Used to log in.
              </Form.Text>
              <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Place Under <span className="text-danger">*</span>
              </Form.Label>
              {placementsLoading ? (
                <div className="text-muted small d-flex align-items-center gap-2">
                  <Spinner size="sm" animation="border" />
                  Loading eligible placements...
                </div>
              ) : placements.length > 0 ? (
                <Form.Select
                  value={form.placement_referral_number}
                  isInvalid={!!errors.placement_referral_number}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      placement_referral_number: e.target.value,
                    }));
                    setErrors((prev) => ({ ...prev, placement_referral_number: "" }));
                  }}
                >
                  <option value="">Select Level 3, 6, or 9 member</option>
                  {placements.map((item) => (
                    <option key={item.referral_number} value={item.referral_number}>
                      {item.name} · Level {item.level} · {item.referral_number}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Alert variant="warning" className="py-2 mb-0">
                  No eligible placement members found at Level 3, 6, or 9 in your network yet.
                </Alert>
              )}
              <Form.Text className="text-muted">
                Sub-IDs can only be placed at Level 3, Level 6, or Level 9.
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {errors.placement_referral_number}
              </Form.Control.Feedback>
            </Form.Group>

            {/* <Form.Group className="mb-3">
              <Form.Label>
                Password <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  placeholder="Minimum 6 characters"
                  isInvalid={!!errors.password}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, password: e.target.value }));
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }}
                />
                <InputGroup.Text
                  role="button"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </InputGroup.Text>
                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>
                Confirm Password <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.password_confirmation}
                  placeholder="Re-enter password"
                  isInvalid={!!errors.password_confirmation}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      password_confirmation: e.target.value,
                    }));
                    setErrors((prev) => ({ ...prev, password_confirmation: "" }));
                  }}
                />
                <InputGroup.Text
                  role="button"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </InputGroup.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.password_confirmation}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group> */}

            <Form.Group className="mb-3" controlId="password">
              <Form.Label className="fw-semibold">
                Password <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup
                className={`flex-nowrap align-items-stretch ${errors.password ? "invalid-input-group" : ""
                  }`}
              >
                <InputGroup.Text
                  className="bg-white"
                  style={{ minHeight: "46px" }}
                >
                  <FontAwesomeIcon icon={faLock} />
                </InputGroup.Text>

                <Form.Control
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  value={form.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password}
                  autoComplete="new-password"
                  className="py-2 border-start-0 border-end-0"
                  style={{
                    boxShadow: "none",
                    minHeight: "46px",
                  }}
                />

                <InputGroup.Text
                  className="p-0"
                  style={{
                    background: "#e9ecef",
                    minWidth: "58px",
                    justifyContent: "center",
                    borderColor: "#dee2e6",
                    minHeight: "46px",
                  }}
                >
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                    style={{
                      background: "transparent",
                      color: "#5f6b7a",
                      minHeight: "46px",
                    }}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup.Text>
              </InputGroup>
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.password}
              </Form.Control.Feedback>

              {form.password && (
                <ul className="mt-2 mb-0 ps-4" style={{ color: "#dc3545" }}>
                  {!passwordRules.uppercase && <li>At least 1 uppercase letter</li>}
                  {!passwordRules.lowercase && <li>At least 1 lowercase letter</li>}
                  {!passwordRules.number && <li>At least 1 number</li>}
                  {!passwordRules.special && <li>At least 1 special character (@$!%*?&)</li>}
                  {!passwordRules.minLength && <li>Minimum 8 characters</li>}
                </ul>
              )}
            </Form.Group>

            <Form.Group className="mb-0" controlId="confirmPassword">
              <Form.Label className="fw-semibold">
                Confirm Password <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup
                className={`flex-nowrap align-items-stretch ${errors.password_confirmation ? "invalid-input-group" : ""
                  }`}
              >
                <InputGroup.Text
                  className="bg-white"
                  style={{ minHeight: "46px" }}
                >
                  <FontAwesomeIcon icon={faLock} />
                </InputGroup.Text>

                <Form.Control
                  name="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter Password"
                  value={form.password_confirmation}
                  onChange={handleChange}
                  isInvalid={!!errors.password_confirmation}
                  autoComplete="new-password"
                  className="py-2 border-start-0 border-end-0"
                  style={{
                    boxShadow: "none",
                    minHeight: "46px",
                  }}
                />

                <InputGroup.Text
                  className="p-0"
                  style={{
                    background: "#e9ecef",
                    minWidth: "58px",
                    justifyContent: "center",
                    borderColor: "#dee2e6",
                    minHeight: "46px",
                  }}
                >
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                    className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                    style={{
                      background: "transparent",
                      color: "#5f6b7a",
                      minHeight: "46px",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEyeSlash : faEye}
                    />
                  </Button>
                </InputGroup.Text>
              </InputGroup>
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.password_confirmation}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="d-inline-flex align-items-center gap-1 act-btn"
                disabled={submitting || usernameCheck.status === "checking" || placementsLoading || placements.length === 0}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" animation="border" /> Adding...
                  </>
                ) : (
                  <>
                    Add ID <FiArrowRight />
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}
